export const APPEARANCE_MODES = ["light", "dark", "system"] as const

export type AppearanceMode = (typeof APPEARANCE_MODES)[number]
export type ResolvedAppearance = "light" | "dark"

export const DEFAULT_APPEARANCE: AppearanceMode = "light"

const USER_KEY_PREFIX = "ezbuild.appearance.user."
// The signed-in user's mode, mirrored so public/appearance-boot.js can paint
// the right theme before the app (and its persisted store) has loaded.
export const ACTIVE_APPEARANCE_KEY = "ezbuild.appearance.active"

export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)"

export const isAppearanceMode = (value: unknown): value is AppearanceMode =>
  typeof value === "string" &&
  (APPEARANCE_MODES as readonly string[]).includes(value)

export const appearanceStorageKey = (userId: string | number) =>
  `${USER_KEY_PREFIX}${userId}`

export const resolveAppearance = (
  mode: AppearanceMode,
  systemPrefersDark: boolean
): ResolvedAppearance =>
  mode === "system" ? (systemPrefersDark ? "dark" : "light") : mode

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">

// Private windows, blocked site data and quota errors all throw here; the
// preference then simply lasts for the page's lifetime.
const safeStorage = (): StorageLike | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export const readAppearance = (
  userId: string | number | null | undefined,
  storage: StorageLike | null = safeStorage()
): AppearanceMode => {
  if (userId == null || !storage) return DEFAULT_APPEARANCE
  try {
    const stored = storage.getItem(appearanceStorageKey(userId))
    return isAppearanceMode(stored) ? stored : DEFAULT_APPEARANCE
  } catch {
    return DEFAULT_APPEARANCE
  }
}

/** Returns false when the browser refused to store it. */
export const writeAppearance = (
  userId: string | number,
  mode: AppearanceMode,
  storage: StorageLike | null = safeStorage()
): boolean => {
  if (!storage) return false
  try {
    storage.setItem(appearanceStorageKey(userId), mode)
    storage.setItem(ACTIVE_APPEARANCE_KEY, mode)
    return true
  } catch {
    return false
  }
}

export const writeActiveAppearance = (
  mode: AppearanceMode | null,
  storage: StorageLike | null = safeStorage()
) => {
  if (!storage) return
  try {
    if (mode) storage.setItem(ACTIVE_APPEARANCE_KEY, mode)
    else storage.removeItem(ACTIVE_APPEARANCE_KEY)
  } catch {
    // Nothing to do: the boot script falls back to light.
  }
}
